/* Mobile feed item card: full-width thumbnail on top, content below, always-visible action buttons.
   Only rendered on mobile viewports (<768px). */

import { memo, useState, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { MoreHorizontal, StickyNote, Star, ShieldOff, Pin, PinOff, Sparkles, Plus, X, Archive, MessageCircle, ListVideo } from "lucide-react";
import { useUserSettings } from "@/hooks/useUserSettings";
import { InlineCompletionCheck, InlineActionButton } from "@/components/feed/CompletionAnimation";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { formatTimeAgo } from "@/lib/formatTimeAgo";
import { formatDuration } from "@/lib/formatDuration";
import { ThumbnailPlaceholder } from "@/components/feed/ThumbnailPlaceholder";
import { useFeedItemNote } from "@/hooks/useFeedItemNote";
import { NoteEditPopover } from "@/components/feed/NoteEditPopover";
import { dispatchChatAboutItem, ANALYZABLE_CONTENT_TYPES } from "@/lib/chatAboutItem";
import type { FeedItemData } from "@/components/feed/FeedItemRow";
import { PodcastPlayButton } from "@/components/player/PodcastPlayButton";
import { YouTubePlayButton } from "@/components/player/YouTubePlayButton";
import { ArticleReadButton } from "@/components/reader/ArticleReadButton";

interface FeedItemMobileCardProps {
  item: FeedItemData;
  onOpen?: () => void;
  onDelete?: () => void;
  onComplete?: () => void;
  onQueue?: () => void;
  onBacklog?: () => void;
  onTagClick?: (tag: string) => void;
  onTopicClick?: (topic: string) => void;
  onToggleSkipProvocation?: () => void;
  onPin?: () => void;
  onUnpin?: () => void;
  onSourceClick?: (sourceId: string) => void;
}

export const FeedItemMobileCard = memo(function FeedItemMobileCard({ item, onOpen, onDelete, onComplete, onQueue, onBacklog, onTagClick, onTopicClick, onToggleSkipProvocation, onPin, onUnpin, onSourceClick }: FeedItemMobileCardProps) {
  const [isPending, setIsPending] = useState(false);
  const [isExitingAnimated, setIsExitingAnimated] = useState(false);
  const deferredActionRef = useRef<(() => void) | null>(null);
  const noteState = useFeedItemNote(item._id, item.note);
  const { animationsEnabled, analyzeContent } = useUserSettings();

  function runAction(action?: () => void) {
    if (!action) return;
    setIsPending(true);
    action();
  }

  // Called when any button animation finishes — starts the exit slide
  const handleActionAnimationDone = useCallback(() => {
    setIsExitingAnimated(true);
  }, []);

  if (isPending) return null;

  return (
    <motion.div
      animate={
        isExitingAnimated
          ? { x: "100%", opacity: 0, height: 0, marginBottom: 0 }
          : { x: 0, opacity: 1, height: "auto" }
      }
      transition={
        isExitingAnimated
          ? {
              x: { duration: 0.25, ease: [0.4, 0, 1, 1] },
              opacity: { duration: 0.1, ease: "easeIn" },
              height: { duration: 0.2, delay: 0.15, ease: [0.22, 1, 0.36, 1] },
            }
          : { duration: 0 }
      }
      className={isExitingAnimated ? "overflow-hidden" : ""}
      onAnimationComplete={() => {
        if (isExitingAnimated) {
          setIsPending(true);
          if (deferredActionRef.current) {
            deferredActionRef.current();
            deferredActionRef.current = null;
          } else {
            onComplete?.();
          }
        }
      }}
    >
    <div data-slot="feed-card" className="flex flex-col gap-2.5 px-4 py-4">
      <div
        className="group relative w-full cursor-pointer"
        style={{ aspectRatio: "16 / 9" }}
        onClick={() => onOpen?.()}
      >
        {item.thumbnail ? (
          <img
            src={item.thumbnail}
            alt={item.title}
            className="w-full h-full rounded-lg object-cover bg-muted"
          />
        ) : (
          <ThumbnailPlaceholder contentType={item.contentType} className="rounded-lg" />
        )}
        {item.contentType === "podcast" && item.audioUrl && (
          <PodcastPlayButton
            track={{
              feedItemId: item._id,
              title: item.title,
              sourceName: item.sourceName,
              thumbnail: item.thumbnail,
              audioUrl: item.audioUrl,
              duration: item.duration,
            }}
          />
        )}
        {item.contentType === "video" && (
          <YouTubePlayButton
            track={{
              feedItemId: item._id,
              title: item.title,
              sourceName: item.sourceName,
              thumbnail: item.thumbnail,
              link: item.link,
              duration: item.duration,
            }}
          />
        )}
        {item.contentType === "article" && (
          <ArticleReadButton feedItemId={item._id} />
        )}
        {item.playlistVideoPosition != null && (
          <span className="absolute top-0 left-0 inline-flex items-center gap-1 bg-muted/90 text-foreground text-xs font-semibold leading-none px-2 py-1 rounded-br-md rounded-tl-sm backdrop-blur-sm">
            <ListVideo className="size-3.5" />
            {item.playlistVideoPosition}{item.playlistTotalVideos != null ? `/${item.playlistTotalVideos}` : ""}
          </span>
        )}
        {item.duration != null && item.duration > 0 && (
          <span className="absolute bottom-1.5 right-1.5 bg-black/80 text-white text-xs font-medium leading-none px-1.5 py-1 rounded">
            {formatDuration(item.duration)}
          </span>
        )}
      </div>

      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        {item.sourceName && (
          item.sourceId && onSourceClick ? (
            <button
              type="button"
              className="text-xs text-muted-foreground hover:text-foreground hover:underline cursor-pointer"
              onClick={() => onSourceClick(item.sourceId!)}
            >
              {item.sourceName}
            </button>
          ) : (
            <span>{item.sourceName}</span>
          )
        )}
        {item.sourceName && <span>·</span>}
        <span>{formatTimeAgo(item.publishedAt)}</span>
        {item.forYouReason && (
          <>
            <span>·</span>
            <span className="inline-flex items-center gap-1 text-[11px] bg-primary/10 text-primary rounded-full px-2 py-0.5 shrink-0">
              <Sparkles className="size-3" />
              {item.forYouReason}
            </span>
          </>
        )}
        <NoteEditPopover
          open={noteState.isEditing}
          onOpenChange={(open) => open ? noteState.startEditing() : noteState.cancelEditing()}
          initialNote={noteState.displayNote}
          onSave={noteState.saveNote}
        >
          {noteState.hasNote ? (
            <button
              type="button"
              className="ml-auto flex items-center gap-1 text-xs text-muted-foreground italic active:opacity-70"
            >
              <StickyNote className="size-3 shrink-0" />
              <span className="truncate max-w-[150px]">{noteState.displayNote}</span>
            </button>
          ) : (
            <button
              type="button"
              className="ml-auto flex items-center gap-1 text-[11px] text-muted-foreground active:opacity-70"
            >
              <StickyNote className="size-3" />
              Add note
            </button>
          )}
        </NoteEditPopover>
      </div>

      <p
        className="text-lg font-medium leading-snug cursor-pointer active:opacity-70"
        onClick={() => onOpen?.()}
      >
        <span className="text-muted-foreground mr-1">→</span>
        {item.sourceStarred && <Star className="inline size-3 fill-current text-primary mr-1 align-baseline" />}
        {item.title}
      </p>

      {item.playlistName && (
        <span className="inline-flex items-center gap-1.5 text-[11px] bg-muted text-muted-foreground rounded-full px-2.5 py-0.5 w-fit">
          <ListVideo className="size-3 shrink-0" />
          {item.playlistName}
        </span>
      )}

      {item.sourceTags && item.sourceTags.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          {item.sourceTags.map((tag) => (
            <button
              key={tag}
              type="button"
              className="text-xs text-muted-foreground hover:text-foreground cursor-pointer transition-colors"
              onClick={() => onTagClick?.(tag)}
            >
              #{tag}
            </button>
          ))}
        </div>
      )}

      {item.aiTopics && item.aiTopics.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap">
          {item.aiTopics.map((topic) => (
            <button
              key={topic}
              type="button"
              className="text-xs text-primary/70 hover:text-primary cursor-pointer transition-colors"
              onClick={() => onTopicClick?.(topic)}
            >
              {topic}
            </button>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        {animationsEnabled ? (
          <div className="flex items-center gap-1 border border-border rounded-md h-7 px-2">
            <InlineCompletionCheck
              variant="twitter-ring"
              onAnimationDone={() => { deferredActionRef.current = onComplete ?? null; handleActionAnimationDone(); }}
            />
            <span className="text-xs">complete</span>
          </div>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="text-xs h-7 px-2"
            onClick={() => runAction(onComplete)}
          >
            <kbd className="opacity-60 mr-1">c</kbd> complete
          </Button>
        )}
        {animationsEnabled ? (
          <div className="flex items-center gap-1 border border-border rounded-md h-7 px-2">
            <InlineActionButton
              type="queue"
              icon={<Plus className="size-3.5" />}
              onAnimationDone={() => { deferredActionRef.current = onQueue ?? null; handleActionAnimationDone(); }}
            />
            <span className="text-xs">queue</span>
          </div>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="text-xs h-7 px-2"
            onClick={() => runAction(onQueue)}
          >
            <kbd className="opacity-60 mr-1">q</kbd> queue
          </Button>
        )}
        {animationsEnabled ? (
          <div className="flex items-center gap-1 border border-border rounded-md h-7 px-2">
            <InlineActionButton
              type="backlog"
              icon={<Archive className="size-3.5" />}
              onAnimationDone={() => { deferredActionRef.current = onBacklog ?? null; handleActionAnimationDone(); }}
            />
            <span className="text-xs">backlog</span>
          </div>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="text-xs h-7 px-2"
            onClick={() => runAction(onBacklog)}
          >
            <kbd className="opacity-60 mr-1">b</kbd> backlog
          </Button>
        )}
        {animationsEnabled ? (
          <div className="flex items-center gap-1 border border-border rounded-md h-7 px-2">
            <InlineActionButton
              type="dismiss"
              icon={<X className="size-3.5" />}
              onAnimationDone={() => { deferredActionRef.current = onDelete ?? null; handleActionAnimationDone(); }}
            />
            <span className="text-xs">dismiss</span>
          </div>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="text-xs h-7 px-2"
            onClick={() => runAction(onDelete)}
          >
            <kbd className="opacity-60 mr-1">x</kbd> dismiss
          </Button>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-7 px-1.5"
            >
              <MoreHorizontal className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {analyzeContent && item.contentType && ANALYZABLE_CONTENT_TYPES.includes(item.contentType) && (
              <DropdownMenuItem onClick={() => dispatchChatAboutItem(item._id, item.title, item.contentType!)}>
                <MessageCircle className="size-4 mr-2" />
                Chat about this
              </DropdownMenuItem>
            )}
            {onPin && (
              <DropdownMenuItem onClick={onPin}>
                <Pin className="size-4 mr-2" />
                Pin to top
              </DropdownMenuItem>
            )}
            {onUnpin && (
              <DropdownMenuItem onClick={onUnpin}>
                <PinOff className="size-4 mr-2" />
                Unpin
              </DropdownMenuItem>
            )}
            {item.sourceId && onToggleSkipProvocation && (
              <DropdownMenuItem onClick={onToggleSkipProvocation}>
                <ShieldOff className="size-4 mr-2" />
                {item.sourceSkipProvocationCheck
                  ? "Re-enable provocative filter for source"
                  : "Exclude source from provocative filter"}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
    </motion.div>
  );
});
